/**
 * VÉRAULT Forms - Form Viewer & Signing Page
 * Client-facing form view with e-signature support
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Loader2, CheckCircle, FileText, AlertCircle, 
  PenTool, Type, ChevronDown, ChevronUp, ExternalLink
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// E-Signature Component with typed and drawn options
function ESignature({ value, onChange, signerName }) {
  const [mode, setMode] = useState('typed'); // 'typed' or 'drawn'
  const [typedSignature, setTypedSignature] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    if (mode === 'typed' && typedSignature) {
      onChange({ type: 'typed', value: typedSignature });
    }
  }, [typedSignature, mode, onChange]);

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1A1A1A';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useEffect(() => {
    if (mode === 'drawn') {
      initCanvas();
    }
  }, [mode]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    setIsDrawing(true);
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    if (isDrawing && hasDrawn) {
      const canvas = canvasRef.current;
      if (canvas) {
        const dataUrl = canvas.toDataURL('image/png');
        onChange({ type: 'drawn', value: dataUrl });
      }
    }
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onChange(null);
  };

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode('typed')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
            mode === 'typed' 
              ? 'bg-[#1A1A1A] text-white' 
              : 'bg-[#F5F1EB] text-[#1A1A1A] hover:bg-[#E6E4DE]'
          }`}
        >
          <Type className="w-4 h-4" />
          Type Signature
        </button>
        <button
          type="button"
          onClick={() => setMode('drawn')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
            mode === 'drawn' 
              ? 'bg-[#1A1A1A] text-white' 
              : 'bg-[#F5F1EB] text-[#1A1A1A] hover:bg-[#E6E4DE]'
          }`}
        >
          <PenTool className="w-4 h-4" />
          Draw Signature
        </button>
      </div>

      {/* Typed Signature */}
      {mode === 'typed' && (
        <div className="space-y-2">
          <Input
            value={typedSignature}
            onChange={(e) => setTypedSignature(e.target.value)}
            placeholder={signerName || "Type your full legal name"}
            className="text-xl font-serif h-14 bg-white border-[#E6E4DE]"
          />
          {typedSignature && (
            <p className="text-sm text-[#8C8C8C]">
              Your typed signature: <span className="font-serif text-lg text-[#1A1A1A]">{typedSignature}</span>
            </p>
          )}
        </div>
      )}

      {/* Drawn Signature */}
      {mode === 'drawn' && (
        <div className="space-y-2">
          <div className="relative">
            <canvas
              ref={canvasRef}
              width={400}
              height={150}
              className="border-2 border-dashed border-[#E6E4DE] rounded-lg bg-white cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            {!hasDrawn && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-[#8C8C8C] text-sm">Sign here</p>
              </div>
            )}
          </div>
          {hasDrawn && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearCanvas}
              className="text-sm"
            >
              Clear & Redraw
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Payment Structure Selection Component
function PaymentStructureInput({ value, onChange }) {
  const [structure, setStructure] = useState(value?.structure || '');
  const [installmentType, setInstallmentType] = useState(value?.installmentType || '');

  useEffect(() => {
    if (structure) {
      onChange({
        structure,
        installmentType: structure === 'installment' ? installmentType : null
      });
    }
  }, [structure, installmentType, onChange]);

  return (
    <div className="space-y-4 p-4 bg-[#F9F8F4] rounded-lg">
      <p className="font-medium text-[#1A1A1A]">Payment Structure (Required)</p>
      
      {/* Main options */}
      <div className="space-y-3">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="payment_structure"
            value="one_time"
            checked={structure === 'one_time'}
            onChange={(e) => setStructure(e.target.value)}
            className="mt-1 w-4 h-4 accent-[#C8A85A]"
          />
          <div>
            <span className="font-medium text-[#1A1A1A]">One-Time Payment</span>
            <p className="text-sm text-[#8C8C8C]">Full $1,000 due upon execution</p>
          </div>
        </label>
        
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="payment_structure"
            value="installment"
            checked={structure === 'installment'}
            onChange={(e) => setStructure(e.target.value)}
            className="mt-1 w-4 h-4 accent-[#C8A85A]"
          />
          <div>
            <span className="font-medium text-[#1A1A1A]">Installment Plan</span>
            <p className="text-sm text-[#8C8C8C]">90+ days to departure; subject to eligibility</p>
          </div>
        </label>
      </div>

      {/* Installment sub-options */}
      {structure === 'installment' && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="ml-7 space-y-3 pt-2 border-t border-[#E6E4DE]"
        >
          <p className="text-sm font-medium text-[#1A1A1A]">Select Installment Type:</p>
          
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="installment_type"
              value="two_payment"
              checked={installmentType === 'two_payment'}
              onChange={(e) => setInstallmentType(e.target.value)}
              className="mt-1 w-4 h-4 accent-[#C8A85A]"
            />
            <div>
              <span className="text-[#1A1A1A]">Two-Payment Plan</span>
              <p className="text-xs text-[#8C8C8C]">$500 to initiate + $500 within 30 days</p>
            </div>
          </label>
          
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="installment_type"
              value="three_payment"
              checked={installmentType === 'three_payment'}
              onChange={(e) => setInstallmentType(e.target.value)}
              className="mt-1 w-4 h-4 accent-[#C8A85A]"
            />
            <div>
              <span className="text-[#1A1A1A]">Three-Payment Plan</span>
              <p className="text-xs text-[#8C8C8C]">Three equal monthly payments</p>
            </div>
          </label>
          
          <p className="text-xs text-[#8C8C8C] italic">
            Final dates will be confirmed in writing and are non-modifiable once planning begins.
          </p>
        </motion.div>
      )}
    </div>
  );
}

// Markdown link renderer component
const MarkdownLink = ({ href, children }) => {
  if (href?.includes('/form/')) {
    return (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-[#C8A85A] hover:underline inline-flex items-center gap-1"
      >
        {children}
        <ExternalLink className="w-3 h-3" />
      </a>
    );
  }
  return <a href={href} className="text-[#C8A85A] hover:underline">{children}</a>;
};

// Markdown component overrides
const markdownComponents = {
  a: MarkdownLink,
  p: ({ children }) => <p className="mb-4 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-4 mb-4 space-y-1">{children}</ul>,
  li: ({ children }) => <li className="text-[#595959]">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1A1A1A]">{children}</strong>,
};

// Main Form Viewer Component
export function FormViewer() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(null);
  const [template, setTemplate] = useState(null);
  const [instance, setInstance] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  
  // Form inputs
  const [paymentStructure, setPaymentStructure] = useState(null);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [signature, setSignature] = useState(null);
  const [acknowledgments, setAcknowledgments] = useState([]);

  useEffect(() => {
    fetchForm();
  }, [token]);

  const fetchForm = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/forms/view/${token}`);
      setInstance(res.data.instance);
      setTemplate(res.data.template);
      
      // Expand all sections by default
      const expanded = {};
      res.data.template.sections?.forEach((s, i) => {
        expanded[s.id] = true;
      });
      setExpandedSections(expanded);
    } catch (err) {
      console.error('Error fetching form:', err);
      toast.error('Form not found or expired');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleAcknowledgmentChange = (ackId, checked) => {
    setAcknowledgments(prev => {
      if (checked) {
        return [...prev, ackId];
      } else {
        return prev.filter(id => id !== ackId);
      }
    });
  };

  const validateForm = () => {
    if (!template) return false;
    
    // Check if signature is required
    if (template.requires_signature) {
      if (!agreementChecked) {
        toast.error('Please agree to the terms');
        return false;
      }
      if (!signature) {
        toast.error('Please provide your signature');
        return false;
      }
      
      // Check payment structure for travel planning agreement
      if (template.id === 'travel_planning_agreement') {
        if (!paymentStructure?.structure) {
          toast.error('Please select a payment structure');
          return false;
        }
        if (paymentStructure.structure === 'installment' && !paymentStructure.installmentType) {
          toast.error('Please select an installment type');
          return false;
        }
      }
    }
    
    // Check if checkbox acknowledgment is required
    if (template.requires_checkbox) {
      if (!acknowledgments.includes(template.id)) {
        toast.error('Please acknowledge the disclosure');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const submitData = {
        form_data: {
          payment_structure: paymentStructure,
          agreement_checked: agreementChecked
        },
        signature: signature,
        acknowledgments: acknowledgments
      };
      
      await axios.post(`${API}/forms/execute/${token}`, submitData);
      
      toast.success('Form submitted successfully!');
      
      // Refresh to show completed state
      await fetchForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F1EB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8A85A]" />
      </div>
    );
  }

  if (!template || !instance) {
    return (
      <div className="min-h-screen bg-[#F5F1EB] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-[#B37E6B] mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#1A1A1A]">Form Not Found</h1>
          <p className="text-[#8C8C8C] mt-2">This form may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  const isCompleted = instance.status === 'completed';
  const mergeData = instance.merge_data || {};

  return (
    <div className="min-h-screen bg-[#F5F1EB]" data-testid="form-viewer">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-[#E6E4DE] py-6 px-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-[#C8A85A] mb-2">VÉRAULT</p>
          <h1 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">{template.name}</h1>
          {mergeData.trip_name && (
            <p className="text-[#8C8C8C] mt-2">For: {mergeData.trip_name}</p>
          )}
          
          {/* Status Badge */}
          {isCompleted && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-[#8DA399]/10 text-[#8DA399] rounded-full">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Completed on {new Date(instance.completed_at).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </header>

      {/* Form Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Sections */}
        <div className="space-y-4">
          {template.sections?.map((section, idx) => (
            <Card key={section.id} className="bg-white border-[#E6E4DE]">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full p-4 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5F1EB] text-[#8C8C8C] text-xs flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <h2 className="font-medium text-[#1A1A1A]">{section.title}</h2>
                </div>
                {expandedSections[section.id] ? (
                  <ChevronUp className="w-5 h-5 text-[#8C8C8C]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#8C8C8C]" />
                )}
              </button>
              
              {expandedSections[section.id] && (
                <CardContent className="pt-0 pb-6 px-4">
                  <div className="prose prose-sm max-w-none text-[#595959]">
                    <ReactMarkdown components={markdownComponents}>
                      {section.content}
                    </ReactMarkdown>
                  </div>
                  
                  {/* Payment Structure Input (Section 3 of Travel Planning Agreement) */}
                  {section.input_type === 'payment_structure' && !isCompleted && (
                    <div className="mt-6">
                      <PaymentStructureInput 
                        value={paymentStructure}
                        onChange={setPaymentStructure}
                      />
                    </div>
                  )}
                  
                  {/* Signature Section */}
                  {section.input_type === 'signature' && !isCompleted && (
                    <div className="mt-6 space-y-6">
                      {/* Agreement Checkbox */}
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                          checked={agreementChecked}
                          onCheckedChange={setAgreementChecked}
                          className="mt-1"
                        />
                        <span className="text-[#1A1A1A]">
                          I have read and agree to the terms of this Travel Planning Agreement.
                        </span>
                      </label>
                      
                      {/* E-Signature */}
                      <div>
                        <p className="font-medium text-[#1A1A1A] mb-3">Electronic Signature</p>
                        <ESignature
                          value={signature}
                          onChange={setSignature}
                          signerName={mergeData.client_full_name}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Acknowledgment Forms (Travel Insurance, Credit Card Auth) */}
        {template.requires_checkbox && !isCompleted && (
          <Card className="mt-6 bg-white border-[#E6E4DE]">
            <CardContent className="p-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={acknowledgments.includes(template.id)}
                  onCheckedChange={(checked) => handleAcknowledgmentChange(template.id, checked)}
                  className="mt-1"
                />
                <span className="text-[#1A1A1A]">
                  {template.acknowledgment_text}
                </span>
              </label>
            </CardContent>
          </Card>
        )}

        {/* Submit Button */}
        {!isCompleted && (
          <div className="mt-8 flex justify-center">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-8 py-3 bg-[#1A1A1A] hover:bg-[#2C2C2C] text-white text-lg"
              data-testid="submit-form-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : template.requires_signature ? (
                'Sign & Submit Agreement'
              ) : (
                'Submit Acknowledgment'
              )}
            </Button>
          </div>
        )}

        {/* Completed State */}
        {isCompleted && (
          <Card className="mt-8 bg-[#8DA399]/10 border-[#8DA399]/20">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-12 h-12 text-[#8DA399] mx-auto mb-4" />
              <h3 className="font-serif text-xl text-[#1A1A1A]">Form Completed</h3>
              <p className="text-[#8C8C8C] mt-2">
                This form was submitted on {new Date(instance.completed_at).toLocaleDateString()} 
                at {new Date(instance.completed_at).toLocaleTimeString()}.
              </p>
              {instance.execution?.signer_name && (
                <p className="text-sm text-[#8C8C8C] mt-2">
                  Signed by: {instance.execution.signer_name}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <footer className="mt-12 text-center text-xs text-[#8C8C8C]">
          <p>VÉRAULT — Luxury Travel Planning</p>
          <p className="mt-1">Form Version: {template.version}</p>
        </footer>
      </main>
    </div>
  );
}

// Reference Form Viewer (for viewing linked documents like T&C)
export function ReferenceFormViewer() {
  const { templateId } = useParams();
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/forms/reference/${templateId}`);
      setTemplate(res.data);
    } catch (err) {
      console.error('Error fetching template:', err);
      toast.error('Document not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F1EB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8A85A]" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen bg-[#F5F1EB] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-[#B37E6B] mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#1A1A1A]">Document Not Found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1EB]" data-testid="reference-form-viewer">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-[#E6E4DE] py-6 px-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-[#C8A85A] mb-2">VÉRAULT</p>
          <h1 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">{template.name}</h1>
          <p className="text-[#8C8C8C] mt-2">{template.description}</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {template.sections?.map((section, idx) => (
          <Card key={section.id} className="mb-4 bg-white border-[#E6E4DE]">
            <CardContent className="p-6">
              {section.title && (
                <h2 className="font-serif text-xl text-[#1A1A1A] mb-4">{section.title}</h2>
              )}
              <div className="prose prose-sm max-w-none text-[#595959]">
                <ReactMarkdown>
                  {section.content}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        ))}

        <footer className="mt-12 text-center text-xs text-[#8C8C8C]">
          <p>VÉRAULT — Luxury Travel Planning</p>
          <p className="mt-1">Document Version: {template.version}</p>
        </footer>
      </main>
    </div>
  );
}

export default FormViewer;
